/**
 * 管理员订单详情
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { AdminOrderDetail as AdminOrderDetailType } from '../../types';
import ConfirmDialog from '../../components/ConfirmDialog';

const formatPrice = (price: number) => (price / 100).toFixed(2);

const statusLabel: Record<string, string> = {
  pending: '待支付',
  paid: '已支付',
  cancelled: '已取消',
  expired: '已过期',
  refunded: '已退费',
};

const refundStatusLabel: Record<string, string> = {
  processing: '退款中',
  success: '已退款',
  failed: '退款失败',
};

const AdminOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<AdminOrderDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchOrder = () => {
    if (!id) return;
    adminApi
      .getOrder(parseInt(id))
      .then((res) => setOrder(res.data.data))
      .catch(() => setError('加载订单信息失败'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const handleRefund = async () => {
    if (!id) return;
    setRefunding(true);
    try {
      const res = await adminApi.refundOrder(parseInt(id));
      setSuccessMsg((res.data as any).message || '退费成功');
      setTimeout(() => setSuccessMsg(''), 5000);
      fetchOrder();
    } catch (err: any) {
      setError(err.response?.data?.detail || '退费失败');
    } finally {
      setRefunding(false);
      setConfirmOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">加载中...</p>
      </div>
    );
  }

  if (!order) {
    return <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">订单不存在</div>;
  }

  return (
    <div>
      <button onClick={() => navigate('/admin/orders')} className="text-sm text-primary hover:text-primary mb-4 inline-block">
        &larr; 返回订单列表
      </button>
      <h2 className="text-xl font-bold text-foreground mb-6">订单详情 #{order.id}</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">{error}</div>
      )}
      {successMsg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">{successMsg}</div>
      )}

      <div className="bg-card rounded-lg shadow p-6">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><span className="text-muted-foreground">订单 ID：</span>{order.id}</div>
          <div><span className="text-muted-foreground">用户：</span>{order.username} (ID: {order.user_id})</div>
          <div><span className="text-muted-foreground">套餐：</span>{order.plan_id}</div>
          <div><span className="text-muted-foreground">金额：</span>¥{formatPrice(order.amount)}</div>
          <div><span className="text-muted-foreground">状态：</span>{statusLabel[order.status] || order.status}</div>
          <div><span className="text-muted-foreground">微信交易流水号：</span>{order.wechat_transaction_id || '-'}</div>
          <div><span className="text-muted-foreground">二维码链接：</span>{order.wechat_qr_code_url || '-'}</div>
          <div><span className="text-muted-foreground">支付时间：</span>{order.paid_at ? new Date(order.paid_at).toLocaleString('zh-CN') : '-'}</div>
          <div><span className="text-muted-foreground">创建时间：</span>{new Date(order.created_at).toLocaleString('zh-CN')}</div>
          <div><span className="text-muted-foreground">更新时间：</span>{new Date(order.updated_at).toLocaleString('zh-CN')}</div>
          {order.refund_status && (
            <>
              <div><span className="text-muted-foreground">退款状态：</span>{refundStatusLabel[order.refund_status] || order.refund_status}</div>
              {order.refund_amount != null && <div><span className="text-muted-foreground">退款金额：</span>¥{formatPrice(order.refund_amount)}</div>}
              {order.wechat_refund_id && <div><span className="text-muted-foreground">微信退款单号：</span>{order.wechat_refund_id}</div>}
              {order.refund_at && <div><span className="text-muted-foreground">退款时间：</span>{new Date(order.refund_at).toLocaleString('zh-CN')}</div>}
            </>
          )}
        </div>

        {order.status === 'paid' && (
          <div className="mt-6 pt-4 border-t border-border">
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={refunding}
              className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {refunding ? '退费中...' : '退费'}
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="确认退费"
        message={`确定要退费 ¥${formatPrice(order.amount)} 吗？此操作将调用微信退款并撤销用户权益。`}
        confirmLabel="确认退费"
        danger
        onConfirm={handleRefund}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default AdminOrderDetail;
